// The song: up to three 4-bar patterns (A–C: e.g. intro, main, outro) and the four sections that
// play them (16 bars, 0:31). Tabs pick the pattern the grid, pads and Wizard work on; the arrangement row picks what
// each section plays. Pattern and section changes are undo steps.
import { LOOP_BARS, MAX_PATTERNS, PATTERN_NAMES, patternCount, patternEvents, sections, SONG_BARS } from "@eb/shared";
import { clock } from "../stream";
import { addPattern, deletePattern, setSection } from "./ops";
import { editSong, useEditor } from "./store";

const { SONG_MS } = clock;

const songLength = `${Math.floor(SONG_MS / 60_000)}:${String(Math.round((SONG_MS % 60_000) / 1000)).padStart(2, "0")}`;

export function SongPanel() {
  const song = useEditor((s) => s.hist.present);
  const pattern = useEditor((s) => s.pattern);
  const readOnly = useEditor((s) => s.readOnly);
  const set = useEditor((s) => s.set);
  const n = patternCount(song);
  const secs = sections(song);
  const add = (copyOf: number | null) => {
    editSong((l) => addPattern(l, copyOf));
    set({ pattern: n });
  };
  return (
    <section className="song-panel panel" aria-label="Song" data-testid="song-panel">
      <div className="song-row">
        <span className="song-label">Patterns</span>
        <div className="pattern-tabs" role="tablist" aria-label="Patterns">
          {Array.from({ length: n }, (_, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={pattern === i}
              className={`pattern-tab${pattern === i ? " on" : ""}`}
              onClick={() => set({ pattern: i })}
              data-testid={`pattern-${PATTERN_NAMES[i]}`}
              title={`Edit pattern ${PATTERN_NAMES[i]} (${patternEvents(song, i).length} notes)`}
            >
              <strong>{PATTERN_NAMES[i]}</strong>
              <span className="pattern-count">{patternEvents(song, i).length}</span>
            </button>
          ))}
        </div>
        {!readOnly && n < MAX_PATTERNS && (
          <>
            <button type="button" className="btn btn-small" onClick={() => add(pattern)} data-testid="pattern-copy">
              + Copy of {PATTERN_NAMES[pattern]}
            </button>
            <button type="button" className="btn btn-small" onClick={() => add(null)} data-testid="pattern-new">
              + Empty
            </button>
          </>
        )}
        {!readOnly && n > 1 && (
          <button
            type="button"
            className="btn btn-small"
            onClick={() => {
              if (window.confirm(`Delete pattern ${PATTERN_NAMES[pattern]}? (You can undo this.)`))
                editSong((l) => deletePattern(l, pattern));
            }}
            data-testid="pattern-delete"
          >
            Delete {PATTERN_NAMES[pattern]}
          </button>
        )}
      </div>
      <div className="song-row">
        <span className="song-label">Song</span>
        <ol className="sections" aria-label={`Arrangement: four sections of ${LOOP_BARS} bars, ${songLength} in all`}>
          {secs.map((p, k) => (
            <li key={k} className="section">
              <span className="section-bars">
                bars {k * LOOP_BARS + 1}–{k * LOOP_BARS + LOOP_BARS}
              </span>
              <div className="segmented" role="radiogroup" aria-label={`Section ${k + 1} plays`}>
                {Array.from({ length: n }, (_, i) => (
                  <button
                    key={i}
                    type="button"
                    role="radio"
                    aria-checked={p === i}
                    className={p === i ? "on" : ""}
                    disabled={readOnly}
                    onClick={() => editSong((l) => setSection(l, k, i))}
                    data-testid={`section-${k}-${PATTERN_NAMES[i]}`}
                  >
                    {PATTERN_NAMES[i]}
                  </button>
                ))}
              </div>
            </li>
          ))}
        </ol>
        <span className="muted song-length">
          {SONG_BARS} bars · {songLength}
        </span>
      </div>
    </section>
  );
}
